import React, { useState } from 'react';
import { formatAmount } from '../utils/format';
import { useTranslation } from 'react-i18next';
import noImage from '../icons/noImageIcon.svg';
import ProductPopup from '../modules/Products/ProductPopup';
import discountIcon from '../icons/discount.svg';



const ProductRow = ({ product }) => {
    const { t } = useTranslation();
    const [imageError, setImageError] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const tva = parseFloat(process.env.REACT_APP_TVA);

    const handleImageError = (e) => {
        setImageError(true);
        e.target.src = noImage;
    };

    // Server-computed, tier-aware unit price (HT) — the reference "before discount"
    // price for this line's quantity (any quantity-tier discount is already baked in).
    const unitPriceHT = product.unitPrice ?? product.product.priceHT;
    const unitPriceTTC = unitPriceHT * (1 + tva);
    const originalTotalPrice = unitPriceTTC * product.quantity;

    // Server-computed line totals already include *every* discount that applied to
    // this line — the product's own flat discount, the per-line sales discount, or
    // both. Rather than re-deriving which one(s) fired (and risking drifting from the
    // backend's actual math), compare the authoritative total against the reference
    // total above: if it's lower, something was discounted.
    const totalPrice = product.priceTTC ?? originalTotalPrice;
    const totalPriceHT = product.priceHT ?? (unitPriceHT * product.quantity);
    const hasDiscount = product.quantity > 0 && totalPrice < originalTotalPrice - 0.01;

    const discountedUnitHT = hasDiscount ? totalPriceHT / product.quantity : unitPriceHT;
    const discountedUnitTTC = hasDiscount ? totalPrice / product.quantity : unitPriceTTC;

    const handleProductClick = (product) => {
        setSelectedProduct(product);
    };

    console.log(product)
    const freeQuantity = product.freeQuantity;

    return (
        <div className="space-y-2">
        <div className="grid grid-cols-[25%_15%_15%_15%_15%_15%] items-center bg-white px-4 py-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
            {/* Product Info */}
            <div className="flex items-center space-x-2"
                onClick={() => handleProductClick(product.product?.id)}
            >
                <div className="flex-none w-16 h-16 bg-white rounded-md shadow-[0px_6px_26px_rgba(98,107,125,0.07)] overflow-hidden flex items-center justify-center">
                    <img
                        src={product.product?.image}
                        alt="-"
                        className={`object-contain ${imageError ? 'w-1/2 h-1/2' : 'w-full h-full'}`}
                        onError={handleImageError}
                    />
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <p className="text-[12px] font-bold text-[#043F88]">{product.product?.reference}</p>
                        {product.product?.discount > 0 && product.discount !== null && (
                            <p className="inline-flex items-center gap-1 text-[12px] bg-[#FFD700] rounded-[12px] px-1 py-0.5 font-bold text-[#043F88]">
                                <img src={discountIcon} alt="discount" className="w-3 h-3" />
                                <span>{product.product?.discount}%</span>
                            </p>
                        )}
                    </div>
                    <p className="text-[#6B78F8] font-semibold">
                        {product.product.name}
                    </p>
                </div>
            </div>

            {/* Quantity */}
            <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">
                    {t('rows.quantity').toUpperCase()}
                </p>
                <p className="text-[#043F88] text-[20px] font-medium">
                    {product.quantity}
                </p>
            </div>

            {/* Discount */}
            <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">
                    {t('rows.discount').toUpperCase()}
                </p>
                <p className="text-[#043F88] text-[20px] font-medium">
                    {product.salesDiscount || 0}%
                </p>
            </div>

            {/* Price HT */}
            <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">
                    {t('rows.priceuHT').toUpperCase()}
                </p>
                {hasDiscount ? (
                    <>
                        <p className="text-[#043F88] text-[20px] font-semibold">
                            {formatAmount(discountedUnitHT)}
                        </p>
                        <p className="text-[#A0A0A0] text-[14px] line-through">
                            {formatAmount(unitPriceHT)}
                        </p>
                    </>
                ) : (
                    <p className="text-[#043F88] text-[20px] font-semibold">
                        {formatAmount(unitPriceHT)}
                    </p>
                )}
            </div>

            {/* Price TTC */}
            <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">
                    {t('rows.priceTTC')}
                </p>
                {hasDiscount ? (
                    <>
                        <p className="text-[#043F88] text-[20px] font-semibold">
                            {formatAmount(discountedUnitTTC)}
                        </p>
                        <p className="text-[#A0A0A0] text-[14px] line-through">
                            {formatAmount(unitPriceTTC)}
                        </p>
                    </>
                ) : (
                    <p className="text-[#043F88] text-[20px] font-semibold">
                        {formatAmount(unitPriceTTC)}
                    </p>
                )}
            </div>

            {/* Total Price */}
            <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">
                    {t('rows.totalPrice')}
                </p>
                {hasDiscount ? (
                    <>
                        <p className="text-[#043F88] text-[20px] font-semibold">
                            {formatAmount(totalPrice)}
                        </p>
                        <p className="text-[#A0A0A0] text-[14px] line-through">
                            {formatAmount(originalTotalPrice)}
                        </p>
                    </>
                ) : (
                    <p className="text-[#043F88] text-[20px] font-semibold">
                        {formatAmount(totalPrice)}
                    </p>
                )}
            </div>

            {selectedProduct && (
                <ProductPopup
                    productId={selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                />
            )}
        </div>
        {!!freeQuantity && (
            <div className="grid grid-cols-[25%_15%_15%_15%_15%_15%] items-center bg-white px-4 py-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                <div className="flex items-center space-x-2">
                    <div className="flex-none w-16 h-16 bg-white rounded-md shadow-[0px_6px_26px_rgba(98,107,125,0.07)] overflow-hidden flex items-center justify-center">
                        <img
                            src={product.product?.image}
                            alt="-"
                            className={`object-contain ${imageError ? 'w-1/2 h-1/2' : 'w-full h-full'}`}
                            onError={handleImageError}
                        />
                    </div>
                    <div>
                        <p className="text-[12px] font-bold text-[#043F88]">{product.product?.reference}</p>
                        <p className="text-[#6B78F8] font-semibold">{product.product?.name}</p>
                    </div>
                </div>
                <div className="text-center">
                    <p className="text-[#707070] text-[12px] font-semibold">{t('rows.quantity').toUpperCase()}</p>
                    <p className="text-[#043F88] text-[20px] font-medium">{freeQuantity}</p>
                </div>
                <div className="text-center">
                    <p className="text-[#707070] text-[12px] font-semibold">{t('rows.discount').toUpperCase()}</p>
                    <p className="text-[#043F88] text-[20px] font-medium">{product.salesDiscount}</p>
                </div>
                <div className="text-center">
                    <p className="text-[#707070] text-[12px] font-semibold">{t('rows.priceuHT').toUpperCase()}</p>
                    <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(0)}</p>
                </div>
                <div className="text-center">
                    <p className="text-[#707070] text-[12px] font-semibold">{t('rows.priceTTC')}</p>
                    <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(0)}</p>
                </div>
                <div className="text-center">
                    <p className="text-[#707070] text-[12px] font-semibold">{t('rows.totalPrice')}</p>
                    <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(0)}</p>
                </div>
            </div>
        )}
        </div>
    );
};

export default ProductRow;
